/**
 * 10.9B — RÓTULO FLUTUANTE 3D. Sprite com texto (pílula) que fica acima de uma estrutura do
 * Santuário (portal PvP, pad de teste, altar…) pra o jogador saber o que é cada uma à distância.
 * Sempre encara a câmera (Sprite) e é legível por cima de tudo (depthTest off).
 */
import * as THREE from "three";

export function makeLabel(text: string, accent = "#2be0ff", height = 4.2, sub?: string): THREE.Sprite {
  // 10.9GU — placa com TÍTULO + SUBTÍTULO opcional (destino/nível), brilho na cor e ponteiro embaixo
  const fs = 46;
  const fsSub = 28;
  const padX = 34;
  const padY = 16;
  const glow = 16; // margem pro brilho não cortar
  const tip = 14; // ponteiro
  const meas = document.createElement("canvas").getContext("2d")!;
  meas.font = `800 ${fs}px system-ui, -apple-system, sans-serif`;
  let tw = Math.ceil(meas.measureText(text).width);
  if (sub) { meas.font = `600 ${fsSub}px system-ui, -apple-system, sans-serif`; tw = Math.max(tw, Math.ceil(meas.measureText(sub).width)); }
  const bw = tw + padX * 2;
  const bh = fs + padY * 2 + (sub ? fsSub + 6 : 0);
  const w = bw + glow * 2;
  const h = bh + glow * 2 + tip;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // brilho + fundo (gradiente escuro com um véu da cor no topo)
  ctx.shadowColor = accent;
  ctx.shadowBlur = glow;
  roundRect(ctx, glow, glow, bw, bh, 24);
  const grad = ctx.createLinearGradient(0, glow, 0, glow + bh);
  grad.addColorStop(0, "rgba(28, 22, 58, 0.94)");
  grad.addColorStop(1, "rgba(7, 9, 20, 0.95)");
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 3.5;
  ctx.strokeStyle = accent;
  ctx.stroke();
  // ponteiro (triângulo) apontando pra estrutura
  ctx.beginPath();
  ctx.moveTo(w / 2 - 13, glow + bh - 1);
  ctx.lineTo(w / 2, glow + bh + tip);
  ctx.lineTo(w / 2 + 13, glow + bh - 1);
  ctx.closePath();
  ctx.fillStyle = accent;
  ctx.fill();
  // filete de luz no topo
  ctx.strokeStyle = "rgba(255,255,255,0.22)";
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(glow + 26, glow + 4); ctx.lineTo(glow + bw - 26, glow + 4); ctx.stroke();
  // título
  ctx.font = `800 ${fs}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = "#f4f7ff";
  ctx.shadowColor = accent; ctx.shadowBlur = 8;
  ctx.fillText(text, w / 2, glow + padY + fs / 2 + 2);
  ctx.shadowBlur = 0;
  if (sub) {
    ctx.font = `600 ${fsSub}px system-ui, -apple-system, sans-serif`;
    ctx.fillStyle = accent;
    ctx.globalAlpha = 0.92;
    ctx.fillText(sub, w / 2, glow + padY + fs + 6 + fsSub / 2);
    ctx.globalAlpha = 1;
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 2;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  const scale = 0.011; // unidades de mundo por pixel do canvas
  sprite.scale.set(w * scale, h * scale, 1);
  sprite.position.y = height;
  sprite.renderOrder = 999; // por cima
  return sprite;
}

/**
 * 10.9BR — descarta um rótulo criado por makeLabel: cada um carrega um canvas + textura + material
 * próprios; só remover da cena vazava a textura na GPU. Quem troca o texto de um rótulo chama isto
 * no antigo antes de criar o novo (BossZone.refresh fazia isso 9× a cada 5 s sem descartar).
 */
export function disposeLabel(sprite: THREE.Object3D | null | undefined): void {
  if (!sprite) return;
  sprite.parent?.remove(sprite);
  const mat = (sprite as THREE.Sprite).material as THREE.SpriteMaterial | undefined;
  if (!mat) return;
  mat.map?.dispose();
  mat.dispose();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
